import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mileage } from '@cwos/api-client';
import {
  ConsequenceNote,
  ErrorPanel,
  Pill,
  SkeletonSet,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { rateDayLabel } from '../mileage/mileageLogic.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Mileage settings.
 *
 * Two of these switches deserve more care than an ordinary setting, and the
 * screen says so rather than presenting eight equal checkboxes:
 *
 *   BACKGROUND DETECTION turns on all-day location capture on employees'
 *   phones. That is the most consequential thing an administrator can enable in
 *   Phibian, so it is stated in full — what is collected, and what the employer
 *   does and does not get to see — and every change is audited.
 *
 *   THE RATE is not defaulted. Phibian ships no statutory figure, because a
 *   wrong default silently underpays people for a year. An organisation with no
 *   rate simply cannot approve mileage, and is told exactly that.
 */
export function MileageSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('mileage:configure');

  const config = useQuery({ queryKey: ['mileage', 'policy'], queryFn: () => mileage.policy() });

  const [draft, setDraft] = useState<mileage.MileagePolicy | null>(null);
  const [cents, setCents] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config.data && draft === null) setDraft(config.data.policy);
  }, [config.data, draft]);

  const save = useMutation({
    mutationFn: (policy: mileage.MileagePolicy) => mileage.updatePolicy(policy),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['mileage', 'policy'] });
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  const openRate = useMutation({
    mutationFn: () => mileage.openRate({ centsPerMile: Number(cents), effectiveFrom }),
    onSuccess: () => {
      setError(null);
      setCents('');
      setEffectiveFrom('');
      void qc.invalidateQueries({ queryKey: ['mileage', 'policy'] });
    },
    onError: (err: unknown) => setError(messageOf(err)),
  });

  // Both boundaries are calendar days ('2026-01-01'), not instants — see
  // `rateDayLabel`, which is why they are not passed to a plain `new Date`.
  const rateColumns: Column<mileage.MileageRate>[] = [
    { key: 'rate', header: 'Rate', render: (r) => `${(r.centsPerMile / 100).toFixed(2)} per mile` },
    { key: 'from', header: 'From', render: (r) => rateDayLabel(r.effectiveFrom) },
    {
      key: 'to',
      header: 'Until',
      render: (r) =>
        r.effectiveTo ? (
          rateDayLabel(r.effectiveTo)
        ) : (
          <Pill variant="good" dot>
            Current
          </Pill>
        ),
    },
  ];

  if (config.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (config.isError && !config.data) {
    return (
      <LoadFailure
        error={config.error}
        message="We couldn’t load mileage settings just now."
        onRetry={() => void config.refetch()}
      />
    );
  }

  const policy = draft ?? config.data!.policy;
  const rates = config.data?.rates ?? [];
  const set = (patch: Partial<mileage.MileagePolicy>) => setDraft({ ...policy, ...patch });

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="Mileage"
        blurb="Phibian notices drives and asks the worker what each one was. You see the ones they mark as work."
      />

      {error ? <ErrorPanel message={error} /> : null}

      <SettingsCard>
        <h2 className="rn-card-title">Tracking</h2>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
          <Toggle
            label="Mileage is on for this organisation"
            checked={policy.enabled}
            disabled={!canManage}
            onChange={(enabled) => set({ enabled })}
          />
          <Toggle
            label="Notice drives automatically in the background"
            checked={policy.backgroundDetectionEnabled}
            disabled={!canManage || !policy.enabled}
            onChange={(backgroundDetectionEnabled) => set({ backgroundDetectionEnabled })}
          />
          {/* The most consequential switch in the product. Stated in full. */}
          <ConsequenceNote
            text={
              'Background detection records your staff’s location throughout their day, on and off the clock — ' +
              'that is the only way to notice a drive nobody started a timer for. Each worker must also grant ' +
              '“Always” location on their own phone. You will only ever see the drives they mark as work, ' +
              'and never the route of any drive: a work drive reaches you as a distance, a time and what ' +
              'else was on the calendar, while personal and unclassified drives reach you as a distance ' +
              'and a date. Nobody here sees where a member of staff actually drove.'
            }
          />
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Reimbursement</h2>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
          <Toggle
            label="Reimburse work mileage"
            checked={policy.reimbursementEnabled}
            disabled={!canManage || !policy.enabled}
            onChange={(reimbursementEnabled) => set({ reimbursementEnabled })}
          />
          <Toggle
            label="A supervisor must approve before anything is reimbursed"
            checked={policy.requiresApproval}
            disabled={!canManage || !policy.reimbursementEnabled}
            onChange={(requiresApproval) => set({ requiresApproval })}
          />
          <Toggle
            label="Do not reimburse travel between home and a work site"
            checked={policy.excludeCommute}
            disabled={!canManage || !policy.reimbursementEnabled}
            onChange={(excludeCommute) => set({ excludeCommute })}
          />
          <ConsequenceNote
            text={
              'A drive the worker marked as work but policy excludes stays a work drive — they are told why it ' +
              'was excluded rather than being told they were wrong.'
            }
          />
          <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
            <span className="rn-muted">Shortest drive worth reimbursing (metres, 0 for none)</span>
            <input
              style={{ marginBottom: 0 }}
              type="number"
              min={0}
              aria-label="Minimum reimbursable distance in metres"
              value={policy.minimumReimbursableMeters}
              disabled={!canManage}
              onChange={(e) => set({ minimumReimbursableMeters: Number(e.target.value) })}
            />
          </label>
        </div>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Manual entries</h2>
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
          <Toggle
            label="Workers may add a drive Phibian missed"
            checked={policy.manualEntryEnabled}
            disabled={!canManage || !policy.enabled}
            onChange={(manualEntryEnabled) => set({ manualEntryEnabled })}
          />
          <Toggle
            label="Manual entries always need review"
            checked={policy.manualRequiresReview}
            disabled={!canManage || !policy.manualEntryEnabled}
            onChange={(manualRequiresReview) => set({ manualRequiresReview })}
          />
          <ConsequenceNote text="A manual entry is the worker’s own account of a drive, not a captured route, and is labelled that way wherever it appears." />
        </div>
      </SettingsCard>

      {canManage ? (
        <div>
          <button
            type="button"
            className="signin-devbtn"
            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
            disabled={save.isPending}
            onClick={() => save.mutate(policy)}
          >
            {save.isPending ? 'Saving…' : 'Save mileage settings'}
          </button>
        </div>
      ) : null}

      <SettingsCard>
        <h2 className="rn-card-title">Rate</h2>
        {rates.length === 0 ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
            You have not set a mileage rate. Phibian does not assume one — a wrong default would
            quietly underpay people — so mileage cannot be approved until you set yours.
          </p>
        ) : (
          <div style={{ marginTop: 'var(--space-12)' }}>
            <TableCard columns={rateColumns} rows={rates} rowKey={(r) => r.id} />
            <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
              Rates are dated. Approving last month’s mileage applies last month’s rate, and an
              approved reimbursement keeps the rate it was paid at.
            </p>
          </div>
        )}

        {canManage ? (
          <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
            <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
              <span className="rn-muted">Cents per mile</span>
              <input
                style={{ marginBottom: 0 }}
                type="number"
                min={0}
                aria-label="Cents per mile"
                value={cents}
                onChange={(e) => setCents(e.target.value)}
              />
            </label>
            <label style={{ display: 'grid', gap: 'var(--space-6)' }}>
              <span className="rn-muted">In force from</span>
              <input
                style={{ marginBottom: 0 }}
                type="date"
                aria-label="Rate effective from"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </label>
            <div>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={openRate.isPending || cents === '' || effectiveFrom === ''}
                onClick={() => openRate.mutate()}
              >
                {openRate.isPending ? 'Saving…' : 'Set this rate'}
              </button>
            </div>
            <ConsequenceNote text="Setting a new rate closes the current one the day before this one starts. Reimbursements already approved keep the rate they were paid at." />
          </div>
        ) : null}
      </SettingsCard>
    </div>
  );
}

function Toggle({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return 'That did not save.';
}
